/* eslint-disable @typescript-eslint/no-empty-function */

import React, { useRef, useState } from 'react'
import {
  Box,
  Color,
  DisplayPosition,
  FontFamily,
  FontWeight,
  IconVariant,
  JustifyContent,
  Text,
  TextAlign,
} from '@gousto-internal/citrus-react'
import { useClickOutside } from '../useClickOutside'
import { CSSObject } from '@emotion/react'
import ReactModal from 'react-modal'
import {
  content,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DropDown,
  IconCustom,
  OptionsButton,
  OuterWrapper,
  overlay,
} from './styles'
import { SupportedOptionType } from '@library/menu-service'
import { RecipeTileHeader, RecipeTileHeaderProps } from '../../RecipeTileHeader'

const escapeKeyPressed = (e: React.KeyboardEvent) => {
  return e.type === 'keyup' && e.code && e.code === 'Escape'
}

const preventPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

type CollapsableProps = {
  children: ({ close, isMobile }: { close: () => void; isMobile: boolean }) => React.ReactNode
  onOpen: () => void
  optionsType: SupportedOptionType
}

export const DesktopCollapsable = ({
  children,
  onOpen,
  optionsType,
  buttonStyles,
}: CollapsableProps & {
  buttonStyles?: CSSObject
}) => {
  const [expanded, setExpanded] = useState(false)

  const selectRef = useRef(null)
  useClickOutside(selectRef, () => setExpanded(false), [expanded])

  return (
    <Box
      position={DisplayPosition.Absolute}
      right="1rem"
      bottom="1rem"
      width="100%"
      onClick={preventPropagation}
    >
      <OuterWrapper ref={selectRef}>
        <OptionsButton
          data-testid="SwapAlternativeOptionsButton"
          buttonStyles={buttonStyles}
          onClick={(e) => {
            e.stopPropagation()
            setExpanded((current) => !current)

            if (!expanded) {
              onOpen()
            }
          }}
          onKeyUp={(e) => {
            e.stopPropagation()

            if (escapeKeyPressed(e)) {
              setExpanded(false)
            }
          }}
        >
          <Text fontFamily={FontFamily.Bold}>
            {optionsType === 'ingredients' ? 'Customise' : 'Options'}
          </Text>
        </OptionsButton>
        {expanded && (
          <DropDown aria-hidden={!expanded} showOptions={expanded}>
            {children({
              close: () => setExpanded(false),
              isMobile: false,
            })}
          </DropDown>
        )}
      </OuterWrapper>
    </Box>
  )
}

type MobileCollapsableProps = CollapsableProps &
  Pick<RecipeTileHeaderProps, 'recipe' | 'currentCollectionId' | 'menuPreferences'>

export const MobileCollapsable = ({
  children,
  onOpen,
  optionsType,
  recipe,
  currentCollectionId,
  menuPreferences,
}: MobileCollapsableProps) => {
  const [showModal, setShowModal] = useState(false)

  const selectRef = useRef(null)
  useClickOutside(selectRef, () => setShowModal(false), [showModal])

  const onClickButton = (
    event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => {
    event.stopPropagation()
    setShowModal(!showModal)

    if (!showModal) {
      onOpen()
    }
  }

  return (
    <Box
      position={DisplayPosition.Absolute}
      bottom="1rem"
      right="1rem"
      width="100%"
      onClick={preventPropagation}
    >
      <Box display="flex" justifyContent={JustifyContent.FlexEnd} style={{ marginLeft: '1rem' }}>
        <Box
          data-testid="SwapAlternativeOptionsMobileButton"
          onClick={onClickButton}
          onKeyDown={onClickButton}
          role="button"
          tabIndex={-1}
        >
          <Text fontFamily={FontFamily.Bold} color={Color.Secondary_400}>
            {optionsType === 'ingredients' ? 'Customise' : 'Options'}
          </Text>
        </Box>
        {showModal && (
          <ReactModal isOpen={showModal} style={{ overlay, content }}>
            <Drawer>
              <DrawerHeader>
                <Text
                  textAlign={TextAlign.Left}
                  fontFamily={FontFamily.Book}
                  size={5}
                  fontWeight={FontWeight.SemiBold}
                >
                  {optionsType === 'ingredients' ? 'Customise' : 'Options available'}
                </Text>
                <IconCustom
                  name="close"
                  size={6}
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowModal(false)
                  }}
                  role="button"
                  variant={IconVariant.Actionable}
                />
              </DrawerHeader>
              {optionsType == 'ingredients' && (
                <Box style={{ paddingTop: '1rem' }}>
                  <RecipeTileHeader
                    recipe={recipe}
                    currentCollectionId={currentCollectionId}
                    menuPreferences={menuPreferences}
                    setBrowserCTAVisible={() => {}}
                    canAddRecipes={false}
                    reachedLimit={false}
                    isInBasket={false}
                    isOutOfStock={false}
                    isProteinOnSignupEnabled={false}
                    index={undefined}
                    showPurchaseInfo={false}
                  />
                </Box>
              )}
              <Box ref={selectRef}>
                <DrawerBody>
                  {children({
                    close: () => setShowModal(false),
                    isMobile: true,
                  })}
                </DrawerBody>
              </Box>
            </Drawer>
          </ReactModal>
        )}
      </Box>
    </Box>
  )
}
